import { ChevronDown, ChevronUp } from 'flowbite-react-icons/outline'
import { CategorizedHPOOption } from '@/types/RequisitionFormTypes'
import React, { useState } from 'react'

interface HPOCategorizationProps {
  categorizedTerms: CategorizedHPOOption[]
  onCategoryChange: (termId: string, newCategory: 'Key' | 'Additional' | 'Excluded') => void
  title?: string
  readOnly?: boolean
}

const HPOCategorization: React.FC<HPOCategorizationProps> = ({
  categorizedTerms,
  onCategoryChange,
  title = 'Categorize New Added HPO Terms',
  readOnly = false,
}) => {
  const [expandedTerms, setExpandedTerms] = useState<Set<string>>(new Set())

  const getCategoryColors = (category: 'Key' | 'Additional' | 'Excluded') => {
    const colors = {
      Key: 'bg-success-100 dark:bg-gray-900 text-text-fg-success-strong dark:text-success-100 border-border-success-subtle dark:border-success-700',
      Additional: 'bg-info-100 dark:bg-gray-900 text-info-600 dark:text-info-100 border-info-500/30 dark:border-info-600',
      Excluded: 'bg-error-100 dark:bg-gray-900 text-text-fg-error-strong dark:text-error-100 border-border-error-subtle dark:border-error-700',
    }
    return colors[category]
  }

  const getFirstWrappedLine = (text: string, maxLength: number = 80): string => {
    // If text is shorter than maxLength, return the whole text
    if (text.length <= maxLength) {
      return text
    }

    // Find the last space before maxLength to avoid cutting words
    const truncated = text.substring(0, maxLength)
    const lastSpaceIndex = truncated.lastIndexOf(' ')

    if (lastSpaceIndex > maxLength * 0.7) {
      // If we found a space in a reasonable position
      return text.substring(0, lastSpaceIndex) + '...'
    } else {
      return truncated + '...'
    }
  }

  const isMultiLine = (text: string): boolean => {
    return text.length > 80 // Show expand button for longer text
  }

  const toggleExpanded = (termId: string) => {
    setExpandedTerms((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(termId)) {
        newSet.delete(termId)
      } else {
        newSet.add(termId)
      }
      return newSet
    })
  }

  if (categorizedTerms.length === 0) {
    return null
  }

  return (
    <div className="mt-4 border-t border-border-base dark:border-gray-700 pt-4">
      <h3 className="text-base mb-2 font-medium text-text-heading dark:text-white">{title}</h3>
      <div className="scrollbar-thin scrollbar-thumb-background-quaternary scrollbar-track-background-tertiary hover:scrollbar-thumb-text-subtle dark:scrollbar-thumb-gray-600 dark:scrollbar-track-gray-800 max-h-64 space-y-3 overflow-y-auto pr-2">
        {categorizedTerms.map((term) => (
          <div
            key={term.id}
            className="flex items-center justify-between rounded-base border border-border-base dark:border-gray-700 bg-background-secondary-medium dark:bg-gray-800 p-3"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-info-600 dark:text-info-100">{term.id}</span>
                <span className="truncate text-sm font-medium text-text-heading dark:text-white">{term.label}</span>
              </div>
              {term.node.meta?.definition?.val && term.node.meta?.definition?.val !== term.label && (
                <div className="mt-1">
                  <p className="text-xs text-text-body dark:text-gray-400">
                    {expandedTerms.has(term.id)
                      ? term.node.meta?.definition?.val
                      : getFirstWrappedLine(term.node.meta?.definition?.val)}
                  </p>
                  {isMultiLine(term.node.meta?.definition?.val) && (
                    <button
                      onClick={() => toggleExpanded(term.id)}
                      className="mt-1 flex items-center gap-1 text-xs text-info-600 dark:text-info-100 hover:underline dark:hover:text-info-100"
                    >
                      {expandedTerms.has(term.id) ? (
                        <>
                          <ChevronUp className="h-3 w-3" />
                          Show less
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-3 w-3" />
                          Show more
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="ml-4 flex items-center gap-2">
              <span className="text-xs text-text-body dark:text-gray-400">Category:</span>
              {readOnly ? (
                <span className={`rounded border px-2 py-1 text-xs ${getCategoryColors(term.category)}`}>
                  {term.category}
                </span>
              ) : (
                <div className="flex gap-1">
                  {(['Key', 'Additional', 'Excluded'] as const).map((cat) => (
                    <button
                      key={cat}
                      onClick={() => onCategoryChange(term.id, cat)}
                      className={`rounded border px-2 py-1 text-xs transition-colors ${
                        term.category === cat
                          ? getCategoryColors(cat)
                          : 'border-border-base-medium dark:border-gray-700 bg-background-tertiary dark:bg-gray-800 text-text-body dark:text-gray-400 hover:bg-background-quaternary dark:hover:bg-gray-700'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default HPOCategorization
